import React, {Component} from 'react'
import propTypes from 'prop-types'
import {isNil, omit, pick} from 'lodash'
import TextField from '@material-ui/core/TextField'
import {parseCssSelector} from 'helpers/css'

const DEBOUNCE_COMMIT_DELAY = 2000

const V0_PROPS = [
  'debouncedCommit',
  'onInputChange',
  'floatingLabelText',
  'hintText',
  'errorText',
  'multiLine',
  'underlineShow',
  'floatingLabelFixed',
  'inputStyle',
  'hintStyle',
  'floatingLabelStyle',
  'floatingLabelFocusStyle',
  'underlineStyle',
  'underlineFocusStyle',
  'underlineDisabledStyle',
  'errorStyle',
  'iconStyle',
  'textareaStyle',
]

class _TextField extends Component {
  constructor(props) {
    super(props)

    this.state = {
      value: this.props.value,
    }
    this.changeTimer = null
  }

  componentDidUpdate(prevProps) {
    if (prevProps.value !== this.props.value) {
      const value = this.props.value
      this.setState({value})
    }
  }

  focus = (forEdit = false) => {
    if (this.input) this.input.focus()
    if (forEdit) this.selectEnd()
  }

  clear = () => {
    this.setState({value: ''})
  }

  selectAll = () => {
    const {value} = this.state
    try {
      this.input.setSelectionRange(0, value.length)
    } catch {
    }
  }

  selectStart = () => {
    try {
      this.input.setSelectionRange(0, 0)
    } catch {
    }
  }

  selectEnd = () => {
    const {value} = this.state
    try {
      this.input.setSelectionRange(value.length, value.length)
    } catch {
    }
  }

  onKeyDown = (e) => {
    const {multiLine, onKeyDown} = this.props
    if (!multiLine && e.keyCode === 13) this.commit() // commit on ENTER
    if (onKeyDown) onKeyDown(e)
  }

  setTimer = () => {
    this.cancelTimer()
    this.changeTimer = window.setTimeout(this.commit, DEBOUNCE_COMMIT_DELAY)
  }

  cancelTimer = () => {
    if (this.changeTimer) window.clearTimeout(this.changeTimer)
    this.changeTimer = null
  }

  onBlur = (e) => {
    const {onBlur} = this.props
    this.commit()  // commit on BLUR
    if (onBlur) onBlur(e)
  }

  onChange = (event) => {
    const value = event.target.value
    const {onInputChange, debouncedCommit} = this.props
    this.setState({value})
    if (onInputChange) onInputChange(event, value)
    if (debouncedCommit) this.setTimer()
  }

  commit = () => {
    this.cancelTimer()

    const {onChange} = this.props
    const {value} = this.state
    if (onChange && (value || '') !== (this.props.value || '')) onChange(value)
  }

  render() {
    const {
      fullHeight,
      textAreaStyles,
      label,
      placeholder,
      // v0 props we translate
      floatingLabelText,
      hintText,
      errorText,
      multiLine,
      underlineShow,
      floatingLabelFixed,
      inputStyle,
      floatingLabelStyle,
    } = this.props
    // HTML input attributes that v0 forwarded to <input> as a top-level prop
    // but v1 requires inside `inputProps`. Hoist them here so consumers don't
    // have to think about it.
    const INPUT_ATTRS = ['maxLength', 'minLength', 'pattern', 'min', 'max', 'step', 'inputMode']
    const passThroughProps = omit(this.props, V0_PROPS.concat(INPUT_ATTRS, [
      'fullHeight',
      'textAreaStyles',
      'label',
      'placeholder',
      'value',
      'onChange',
      'onBlur',
      'onKeyDown',
      'inputProps',
      'readOnly',
    ]))
    const hoistedInputAttrs = pick(this.props, INPUT_ATTRS)
    const consumerInputProps = this.props.inputProps || {}
    // v1 TextField has no top-level `readOnly` prop; if passed it lands on the
    // FormControl root div as a stray HTML attribute. Hoist it into InputProps
    // so it reaches the underlying input.
    const consumerReadOnly = this.props.readOnly

    let {value} = this.state
    if (isNil(value)) value = '' // pass the value as empty string when the value is nil to fix prop type warnings

    const style = fullHeight ? {height: '100%', display: 'flex', flexDirection: 'column'} : undefined

    // Map style props for the underlying <input>/<textarea>. v1's multiline
    // textarea auto-resizes with content; we let that natural behaviour apply
    // and rely on parent scrolling for overflow rather than constraining height.
    const inputElementStyle = multiLine
      ? {...inputStyle, ...(textAreaStyles || {})}
      : inputStyle

    const InputProps = ((underlineShow === false) || consumerReadOnly)
      ? {
        ...(underlineShow === false ? {disableUnderline: true} : {}),
        ...(consumerReadOnly ? {readOnly: true} : {}),
      }
      : undefined

    const InputLabelProps = (floatingLabelFixed || floatingLabelStyle)
      ? {
        ...(floatingLabelFixed ? {shrink: true} : {}),
        ...(floatingLabelStyle ? {style: floatingLabelStyle} : {}),
      }
      : undefined

    // Emulate v0 typography cascade. v0 TextField let CSS inheritance from `style`
    // reach the underlying <input>; v1 sets typography on the input via classes,
    // overriding cascade. Mirror typography props from `style` onto the input.
    const TYPOGRAPHY_STYLE_PROPS = [
      'fontSize', 'lineHeight', 'fontWeight', 'fontFamily', 'color', 'letterSpacing', 'textTransform',
    ]
    const cascadedTypography = pick(this.props.style || {}, TYPOGRAPHY_STYLE_PROPS)

    const inputPropsStyle = {...cascadedTypography, ...inputElementStyle}
    const mergedInputProps = {
      ...(Object.keys(inputPropsStyle).length > 0 ? {style: inputPropsStyle} : {}),
      ...hoistedInputAttrs,
      ...consumerInputProps,
    }
    const hasInputProps = Object.keys(mergedInputProps).length > 0

    // Generate an ID for Material UI as it needs it
    const id = `${Math.random() * 1000000000000}`

    return (
      <div className={`memoField-input-${parseCssSelector(label)}`}>
        <TextField
          id={id}
          inputRef={e => this.input = e}
          style={style}
          label={floatingLabelText}
          margin={floatingLabelText ? 'normal' : 'none'}
          placeholder={hintText !== undefined ? hintText : placeholder}
          error={!!errorText}
          helperText={errorText || undefined}
          multiline={!!multiLine}
          {...(InputProps && {InputProps})}
          {...(InputLabelProps && {InputLabelProps})}
          {...(hasInputProps && {inputProps: mergedInputProps})}
          {...passThroughProps}
          onChange={this.onChange}
          onBlur={this.onBlur}
          onKeyDown={this.onKeyDown}
          value={value}
        />
      </div>
    )
  }
}

_TextField.propTypes = {
  className: propTypes.string,
  debouncedCommit: propTypes.bool, // set to true to receive delayed commit() callbacks after changes
  errorText: propTypes.node,
  floatingLabelFixed: propTypes.bool,
  floatingLabelStyle: propTypes.object,
  floatingLabelText: propTypes.node,
  fullHeight: propTypes.bool,
  fullWidth: propTypes.bool,
  hintText: propTypes.node,
  inputProps: propTypes.object,
  inputStyle: propTypes.object,
  label: propTypes.any,
  multiLine: propTypes.bool,
  onBlur: propTypes.func,
  onChange: propTypes.func,
  onInputChange: propTypes.func,
  onKeyDown: propTypes.func,
  placeholder: propTypes.string,
  readOnly: propTypes.bool,
  style: propTypes.object,
  textAreaStyles: propTypes.object,
  underlineShow: propTypes.bool,
  value: propTypes.string,
}

_TextField.defaultProps = {
  debouncedCommit: false,
  floatingLabelFixed: false,
  fullHeight: false,
  multiLine: false,
}

export default _TextField
